import type { GameState, Piece, Side, Square, VictoryResult } from "./types";
import { fromKey } from "./coords";
import { isInGreaterVictoryArea } from "./constants";
import {
  findGloriousSet,
  findGreaterTriple,
  findRoyalSet,
} from "./progressions";

interface PieceInTerritory {
  piece: Piece;
  square: Square;
}

/**
 * Fulke V1 strict triumph zone: the opponent's first five ranks
 * (white triumphs in ranks 11..15; black triumphs in ranks 0..4).
 */
function ownPiecesInTriumphZone(state: GameState, side: Side): PieceInTerritory[] {
  const out: PieceInTerritory[] = [];
  for (const [key, piece] of state.board) {
    if (piece.side !== side) continue;
    const sq = fromKey(key);
    if (isInGreaterVictoryArea(side, sq.rank)) out.push({ piece, square: sq });
  }
  return out;
}

/**
 * Fulke V1 prerequisite: triumph cannot be claimed until the enemy Pyramid (king) is taken.
 * Per Fulke: "The game is never wonne, untyll the king be taken."
 */
function enemyPyramidStillOnBoard(state: GameState, side: Side): boolean {
  const enemy: Side = side === "white" ? "black" : "white";
  for (const piece of state.board.values()) {
    if (piece.side === enemy && piece.shape === "pyramid") return true;
  }
  return false;
}

function sumValues(pieces: readonly Piece[]): number {
  return pieces.reduce((acc, p) => acc + p.value, 0);
}

function distinctShapes(pieces: readonly Piece[]): number {
  return new Set(pieces.map((p) => p.shape)).size;
}

function checkCommon(state: GameState, side: Side): VictoryResult | null {
  const captures = state.captured[side];
  const total = sumValues(captures);
  if (total >= state.victoryConfig.commonThreshold) {
    return {
      level: "common",
      side,
      detail: `${side === "white" ? "White" : "Black"} captured ${total} points of their opponent's pieces (per-side threshold ${state.victoryConfig.commonThreshold})`,
    };
  }
  return null;
}

function checkProper(state: GameState, side: Side): VictoryResult | null {
  const captures = state.captured[side];
  if (captures.length < state.victoryConfig.properMinPieces) return null;
  if (distinctShapes(captures) < state.victoryConfig.properMinShapes) return null;
  const total = sumValues(captures);
  if (total < state.victoryConfig.commonThreshold) return null;
  return {
    level: "proper",
    side,
    detail: `Proper Victory: ${captures.length} pieces (${distinctShapes(captures)} shapes), ${total} points`,
  };
}

function checkGreater(state: GameState, side: Side): VictoryResult | null {
  // V1 Fulke prerequisite — enemy Pyramid must be off the board first.
  if (enemyPyramidStillOnBoard(state, side)) return null;
  const items = ownPiecesInTriumphZone(state, side);
  if (items.length < 3) return null;
  const find = findGreaterTriple(items, (it) => it.piece.value);
  if (!find) return null;
  return {
    level: "greater",
    side,
    detail: `Greater Victory: ${find.type} progression ${find.members.map((m) => m.piece.value).sort((a, b) => a - b).join(", ")} in the opponent's first five ranks`,
    squares: find.members.map((m) => m.square),
  };
}

function checkRoyal(state: GameState, side: Side): VictoryResult | null {
  if (enemyPyramidStillOnBoard(state, side)) return null;
  const items = ownPiecesInTriumphZone(state, side);
  if (items.length < 4) return null;
  const set = findRoyalSet(items, (it) => it.piece.value);
  if (!set) return null;
  return {
    level: "royal",
    side,
    detail: `Royal Victory: 2 progression types within {${set.map((m) => m.piece.value).sort((a, b) => a - b).join(", ")}} in the opponent's first five ranks`,
    squares: set.map((m) => m.square),
  };
}

function checkGlorious(state: GameState, side: Side): VictoryResult | null {
  if (enemyPyramidStillOnBoard(state, side)) return null;
  const items = ownPiecesInTriumphZone(state, side);
  if (items.length < 4) return null;
  const set = findGloriousSet(items, (it) => it.piece.value);
  if (!set) return null;
  return {
    level: "glorious",
    side,
    detail: `Glorious Victory: arithmetic, geometric, and harmonic progressions within {${set.map((m) => m.piece.value).sort((a, b) => a - b).join(", ")}} in the opponent's first five ranks`,
    squares: set.map((m) => m.square),
  };
}

/** Check whether `side` has won at the configured victory level. */
export function checkVictory(state: GameState, side: Side): VictoryResult | null {
  switch (state.victoryConfig.level) {
    case "common":
      return checkCommon(state, side);
    case "proper":
      return checkProper(state, side);
    case "greater":
      return checkGreater(state, side);
    case "royal":
      return checkRoyal(state, side);
    case "glorious":
      return checkGlorious(state, side);
  }
}

/**
 * Check ALL levels for `side`. Useful for UI status display ("you would have won at Common!").
 * Returns the highest-tier achievement, in priority order: glorious > royal > greater > proper > common.
 */
export function checkAllLevels(state: GameState, side: Side): VictoryResult[] {
  const results: VictoryResult[] = [];
  for (const fn of [checkGlorious, checkRoyal, checkGreater, checkProper, checkCommon]) {
    const r = fn(state, side);
    if (r) results.push(r);
  }
  return results;
}
